import createTheme, {neutralPalette} from "./index";
import {createMuiTheme, fade} from "@material-ui/core";
import {CDN_BRAND_PATH, CUSTOM_SITE_SKIN_ENABLED, CUSTOM_SITE_SKIN_NAME, IMAGES_PATH, IS_DEV, SHARED_CDN_PATH} from "Setup/config";

const primaryPalette = {
    main: "#FF6A6A",
    contrastText: "#fff"
}

const secondaryPalette = {
    main: "#7490FC",
    contrastText: "#fff"
}

const layoutPalette = {
    xlight: "#FFF",
    light: "#FFF0F3",
    main: "#FF6A6A",
    dark: "#FB4343",
    xdark: "#9F0808",
    contrastText: "#fff",
    alternateContrastText: '#484848'
}

const headersFontFamily = ['Mada', 'sans-serif'].join(',');
const bodyFontFamily = ['Roboto', 'sans-serif'].join(',');
let headersTypography = {};
['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach(header => headersTypography[header] = {fontFamily: headersFontFamily, fontWeight: 600})

const theme = createMuiTheme();
const brandId = 'dotty-uk';
const IMAGE_PATH_REWRITE = IS_DEV ? IMAGES_PATH.replace(CDN_BRAND_PATH, brandId) : IMAGES_PATH;
const dottyBingoTheme = createTheme({
        palette: {
            primary: primaryPalette,
            secondary: secondaryPalette,
            layout: layoutPalette
        },
        layout: {
            brandId,
            brandPath: `${IMAGE_PATH_REWRITE}/`,
            logo: `${IMAGE_PATH_REWRITE}/logo.png`,
            contrastLogo: `${IMAGE_PATH_REWRITE}/logo-contrast.png`,
            background: CUSTOM_SITE_SKIN_ENABLED ? `url('${IMAGE_PATH_REWRITE}/skins/${CUSTOM_SITE_SKIN_NAME}.webp') repeat-y center top/100%` : 'none',
        },
        overrides: {
            Buttons: {
                // If we need to reverse the buttons
                primary: 'secondary',
                secondary: 'primary',
            },
            TopBar: {
                logoWidth: 95,
                logoWidthSmall: 85,
            },
            PlayerBar: {
                background: layoutPalette.xlight,
                contrastText: layoutPalette.alternateContrastText,
                borderBottom: `1px solid ${layoutPalette.dark}`,
                inputBackground: layoutPalette.xlight,
                inputColor: layoutPalette.alternateContrastText,
            },
            BingoCards: {
                pjpBarBackgroundColor: "#FFB500"
            },
            LoginPage: {
                sideLogoWidth: 150,
            },
            NotificationsPanel: {
                background: neutralPalette.xlight,
                contrastText: layoutPalette.alternateContrastText,
                notificationIconHover: layoutPalette.xdark,
                notificationIconColor: primaryPalette.main,
                badge: "white",
            },
            GamificationModal: {
                textColor: "black",
                backgroundColor: layoutPalette.xlight,
            },
            Content: {
                contrastText: layoutPalette.alternateContrastText,
            },
            LobbyNavigation: {
                background: layoutPalette.dark,
                defaultText: neutralPalette.xlight,
                contrastText: neutralPalette.xlight,
                contrastTextHover: neutralPalette.xlight,
            },
            LobbyGameTiles: {
                tileSpacing: theme.spacing(0.25),
                borderRadius: theme.spacing(1),
                borderPadding: theme.spacing(0.5),
            },
            Footer: {
                background: layoutPalette.dark,
                opacity: 0.9
            },
            SideBarMenu: {
                headerBackground: layoutPalette.dark,
                background: layoutPalette.contrastText,
                contrastText: layoutPalette.alternateContrastText,
                iconColor: layoutPalette.xdark,
            },
            DailyDealV2Modal: {
                borderColor: "#A12C34"
            },
            AccountSideMenu: {
                background: layoutPalette.main,
                contrastText: layoutPalette.contrastText,
                backdropFilter: 'unset',
                dividerColor: neutralPalette.xlight,
                accountSectionContainer: {
                    background: layoutPalette.contrastText,
                    headerBackground: layoutPalette.xdark,
                    contrastText: neutralPalette.xlight,
                },
                navigationItems: {
                    color: layoutPalette.alternateContrastText,
                    background: layoutPalette.contrastText,
                },
                balanceBox: {
                    color: layoutPalette.contrastText,
                    background: layoutPalette.dark,
                    dividerColor: '#ff7c96',
                },
                message: {
                    background: '#E7E6E8',
                    color: layoutPalette.alternateContrastText,
                    iconColor: layoutPalette.main,
                    hoverColor: layoutPalette.xdark,
                    dividerColor: layoutPalette.main,
                },
            },
            rgTools: {
                logo: `${IMAGE_PATH_REWRITE}/logo-contrast.png`,
                background: '#fff0f3',
                contentBackground: neutralPalette.xlight,
                navLinkBackground: layoutPalette.xlight,
                iconColor: layoutPalette.xdark,
                contrastText: neutralPalette.xdark,
                sideBarHeader: layoutPalette.xdark,
                buttonBorderRadius: theme.spacing(2),
                linkColor: layoutPalette.xdark,
                activeLink: layoutPalette.light,
            },
            HoldingPage: {
                backgroundColor: layoutPalette.xdark,
            },
            LandingPage: {
                background: `#F8F6F6 url('${IMAGE_PATH_REWRITE}/offer-page/bg.webp') center bottom / cover`,
                backgroundColor: '#F8F6F6',
                backgroundMobile: layoutPalette.dark,
                logoWidth: 130,
                logoWidthSmall: 100,
                jackpotBackground: "#fff",
                jackpotTextColor: '#333',
                slotMachinebackground: '#333',
                offer: {
                    fontFamily: ['Helvetica', 'Source Sans Pro', 'sans-serif',].join(','),
                    color: '#ff6a6a',
                    fontWeight: 800,
                }
            },
            NewPlayerHomepage: {
                background: `#fafafa url('${IMAGE_PATH_REWRITE}/new-player-homepage/bg.webp') center bottom / cover`,
                backgroundMobile: `#fb4343 url('${IMAGE_PATH_REWRITE}/new-player-homepage/bg.webp') -1015px top / 500%`,
                logoWidth: 125,
                logoWidthSmall: 90,
            },
            TrustPilotSection: {
                backgroundMobile: '#fafafa',
            },
            ShortForm: {
                background: 'rgba(255, 255, 255, 0.2)',
                inputColor: layoutPalette.main,
            },
            BankingModal: {
                backgroundColor: "white",
                actionTabColor: "white"
            },
            filterTab: {
                border: `solid 1px #97B9FF`,
                background: '#F6F7F8',
                color: secondaryPalette.main,
                activeBackground: '#385493',
                activeColor: layoutPalette.contrastText,
            },
            RewardsPage: {
                background: layoutPalette.light,
                headerBackground: `url('${IMAGE_PATH_REWRITE}/rewards/bg.png')`,
                showLevelTextBeneathIcon: false,
                indicator: {
                    background: secondaryPalette.main,
                },
                tabs: {
                    background: secondaryPalette.main,
                    activeColor: "#fff",
                    inactiveColor: "#000",
                },
                cards: {
                    background: "white",
                    textColor: "black",
                },
                infoCards: {
                    color: 'white',
                    background: layoutPalette.main,
                    ctaColor: 'white',
                    borderColor: 'white',
                }
            },
            LevelProgressBar: {
                fillColor: "linear-gradient(180deg, #FEE4A7 50%, #FFB500 50.01%)",
            },
            RegistrationForm: {
                buttonGroupBackground: 'rgb(116, 144, 252)',
                steps: {
                    completeColor: 'rgb(80, 120, 209)',
                },
            },
            PromotionCard: {
                borderRadius: theme.spacing(2),
                background: layoutPalette.xlight,
                contrastText: layoutPalette.alternateContrastText
            },
            RGInfoPage: {
                root: {
                    background: `url(${SHARED_CDN_PATH}/GenericImages/tic-tac-toe.png), linear-gradient(to bottom, ${layoutPalette.main}, ${layoutPalette.dark})`
                },
                topBar: {
                    color: layoutPalette.light,
                    hoverColor: layoutPalette.contrastText,
                    menuIconColor: layoutPalette.contrastText
                },
            },
            LoadingModal: {
                sprite: {
                    width: 123,
                    height: 12750,
                    canvasHeight: 160,
                    steps: '2.3s steps(75) infinite',
                }
            },
            ChangelogPage: {
                background: '#F6F7F8',
                textColor: '#5078D1'
            },
            /* MUI Overrides */
            MuiButton: {
                root: {
                    borderRadius: 24
                },
                outlined: {
                    borderWidth: "2px !important",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                }
            },
            MuiOutlinedInput: {
                root: {
                    "&:hover $notchedOutline": {
                        borderColor: fade(secondaryPalette.main, 0.4),
                    },
                    "&$focused $notchedOutline": {
                        borderColor: fade(secondaryPalette.main, 0.8),
                    }
                }
            },
            MuiCssBaseline: {
                '@global': {
                    'a:not(.MuiButtonBase-root, .MuiTypography-root)': {
                        color: layoutPalette.light,
                        textDecoration: "underline"
                    },
                    '.MuiPaper-root a:not(.MuiButtonBase-root, .MuiTypography-root)': {
                        color: 'rgb(23, 121, 186)',
                        textDecoration: "underline"
                    },
                },
            },
        },
        // shadows: ["none"],
        typography: {
            fontFamily: bodyFontFamily,
            ...headersTypography,
            fontWeightLight: 400,
            button: {
                fontFamily: bodyFontFamily,
            },
        },
        shape: {
            borderRadius: 12
        }
    }
);

export default dottyBingoTheme;
